import { useId } from "react";
import { MOOD_BY_KEY } from "./moods";
import "./moods.css";

// The 12 hand-drawn mood faces, each a custom inline SVG (never an emoji
// glyph). A 44r circle with a radial gradient from the light stop to the
// deep stop, a soft top-left highlight, then features stroked in a warm
// near-black. "on one" is a flame body instead of a circle. Faces animate
// with a gentle idle loop and are never dimmed. Gradient ids are made
// unique per instance so many faces can share a page.

function Base({ gid, c1, c2, behind, children }) {
  return (
    <>
      <defs>
        <radialGradient id={gid} cx="34%" cy="26%" r="84%">
          <stop offset="0" stopColor={c1} />
          <stop offset="1" stopColor={c2} />
        </radialGradient>
      </defs>
      {behind}
      <circle cx="50" cy="50" r="44" fill={`url(#${gid})`} />
      <ellipse cx="37" cy="30" rx="12" ry="7" fill="rgba(255,255,255,.4)" />
      {children}
    </>
  );
}

const STROKE = { fill: "none", stroke: "#2A2440", strokeWidth: 4.2, strokeLinecap: "round", strokeLinejoin: "round" };

function renderFace(key, gid, m) {
  const { c1, c2 } = m;
  switch (key) {
    case "relief":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <g {...STROKE}>
            <path d="M30 44Q38 50 46 44" />
            <path d="M54 44Q62 50 70 44" />
            <ellipse cx="50" cy="66" rx="5.4" ry="7" />
          </g>
          <path d="M74 58q7-2 4-8q-3-5 4-8" fill="none" stroke="rgba(255,255,255,.75)" strokeWidth="3" strokeLinecap="round" />
        </Base>
      );
    case "onone":
      return (
        <>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={c1} />
              <stop offset="1" stopColor={c2} />
            </linearGradient>
          </defs>
          <path d="M50 10C60 30 76 38 71 60C68 82 58 91 50 91C42 91 32 82 29 60C24 38 40 32 50 10Z" fill={`url(#${gid})`} />
          <path d="M50 36C56 47 63 51 60 62C58 73 54 77 50 77C46 77 42 73 40 62C37 51 44 47 50 36Z" fill="rgba(255,244,222,.55)" />
          <g fill="#2A2440"><circle cx="43" cy="60" r="2.7" /><circle cx="57" cy="60" r="2.7" /></g>
          <path d="M45 71H55" fill="none" stroke="#2A2440" strokeWidth="4.2" strokeLinecap="round" />
        </>
      );
    case "chill":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <g fill="#2A2440"><rect x="26" y="40" width="19" height="13" rx="5" /><rect x="55" y="40" width="19" height="13" rx="5" /></g>
          <g fill="none" stroke="#2A2440" strokeWidth="4.2" strokeLinecap="round"><path d="M45 45H55" /><path d="M37 66Q50 74 63 66" /></g>
        </Base>
      );
    case "melting":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <path className="mf-drip" d="M77 60q6 9 0 15q-6-6 0-15Z" fill={`url(#${gid})`} />
          <g {...STROKE}>
            <path d="M30 46Q38 50 46 46" />
            <path d="M54 46Q62 50 70 46" />
            <path d="M36 67Q42 62 48 67Q54 72 60 67" />
          </g>
        </Base>
      );
    case "cooked":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <g fill="none" stroke="#2A2440" strokeWidth="4.2" strokeLinecap="round">
            <path d="M32 40L42 50M42 40L32 50" />
            <path d="M58 40L68 50M68 40L58 50" />
            <circle cx="50" cy="69" r="5" />
          </g>
        </Base>
      );
    case "rough":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <g {...STROKE}>
            <path d="M31 43L45 39" />
            <path d="M69 43L55 39" />
            <path d="M38 71Q50 63 62 71" />
          </g>
          <g fill="#2A2440"><circle cx="39" cy="51" r="2.6" /><circle cx="61" cy="51" r="2.6" /></g>
        </Base>
      );
    case "thriving":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <g {...STROKE}>
            <path d="M30 45Q38 38 46 45" />
            <path d="M54 45Q62 38 70 45" />
            <path d="M33 60Q50 79 67 60" />
          </g>
        </Base>
      );
    case "soft":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <g fill="#E8355F">
            <path d="M36 41c-2-4-8-3-7 2 .8 3.6 5 5.4 7 7.4 2-2 6.2-3.8 7-7.4 1-5-5-6-7-2Z" />
            <path d="M64 41c-2-4-8-3-7 2 .8 3.6 5 5.4 7 7.4 2-2 6.2-3.8 7-7.4 1-5-5-6-7-2Z" />
          </g>
          <path d="M40 66Q50 72 60 66" fill="none" stroke="#2A2440" strokeWidth="4.2" strokeLinecap="round" />
        </Base>
      );
    case "meh":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <g fill="#2A2440"><circle cx="38" cy="46" r="3" /><circle cx="62" cy="46" r="3" /></g>
          <path d="M40 66H60" fill="none" stroke="#2A2440" strokeWidth="4.2" strokeLinecap="round" />
        </Base>
      );
    case "salty":
      return (
        <Base
          gid={gid}
          c1={c1}
          c2={c2}
          behind={<g fill="rgba(255,255,255,.55)"><circle cx="21" cy="21" r="4" /><circle cx="27" cy="16" r="3" /><circle cx="79" cy="21" r="4" /><circle cx="73" cy="16" r="3" /></g>}
        >
          <g {...STROKE}>
            <path d="M30 40L45 47" />
            <path d="M70 40L55 47" />
            <path d="M42 71Q50 65 58 71" />
          </g>
          <g fill="#2A2440"><circle cx="40" cy="53" r="2.6" /><circle cx="60" cy="53" r="2.6" /></g>
        </Base>
      );
    case "drained":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <g {...STROKE}>
            <path d="M30 47Q38 51 46 47" />
            <path d="M54 47Q62 51 70 47" />
            <path d="M42 66H58" />
          </g>
          <g fill="none" stroke="#2A2440" strokeLinecap="round" strokeLinejoin="round">
            <path d="M68 30h6l-6 7h6" strokeWidth="3" />
            <path d="M78 18h5l-5 6h5" strokeWidth="2.4" />
          </g>
        </Base>
      );
    case "shambles":
      return (
        <Base gid={gid} c1={c1} c2={c2}>
          <g {...STROKE}>
            <path d="M31 41Q38 36 45 41" />
            <path d="M55 41Q62 36 69 41" />
            <path d="M39 70Q50 61 61 70Q50 79 39 70Z" />
          </g>
          <g fill="#2A2440"><circle cx="39" cy="51" r="2.6" /><circle cx="61" cy="51" r="2.6" /></g>
          <path className="mf-tear" d="M66 57q5 8 0 13q-5-5 0-13Z" fill="#7FD8F5" />
        </Base>
      );
    default:
      return null;
  }
}

export default function MoodFace({ moodKey, size = 40, animate = true, className = "" }) {
  const rid = useId().replace(/[:]/g, "");
  const m = MOOD_BY_KEY[moodKey];
  if (!m) return null;
  const gid = `mfg-${moodKey}-${rid}`;
  const cls = `mf-face ${animate ? `mf-anim mf-a-${m.anim}` : ""} ${className}`.trim();
  const style = animate ? { animationDuration: `${m.dur}s` } : undefined;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={cls} style={style} aria-hidden="true" role="img">
      {renderFace(moodKey, gid, m)}
    </svg>
  );
}
